import { inject, Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import { AssetInstance } from '../models/asset-instance.model';
import { LabelLayout, LabelLayoutConfig } from '../models/label-layout.model';
import { SheetFormat } from '../models/sheet-format.model';
import { LoggerService } from './logger.service';

export interface LabelPdfParams {
  sheetFormat: SheetFormat;
  layout: LabelLayout;
  /** Set of 0-based position indices (row * columns + col) to print */
  selectedPositions: Set<number>;
  checkpointName: string;
  formName: string;
  /** Full URL for the QR code content */
  qrUrl: string;
  /** Code type: 'qr' (default) or 'barcode' */
  codeType?: 'qr' | 'barcode';
  /** Barcode values per position: position index → barcode value string. Used when codeType is 'barcode'. */
  barcodeValues?: Map<number, string>;
  /** Shared barcode value for non-instanced barcode labels */
  sharedBarcodeValue?: string;
  /** Whether checkpoint-name text regions should be rendered */
  showCheckpointName?: boolean;
  /** Whether form-name text regions should be rendered */
  showFormName?: boolean;
  /** Whether code regions should be rendered */
  showCode?: boolean;
  /** Whether serial-number text regions should be rendered (only for instanced labels) */
  showSerialNumber?: boolean;
  /** Font size override for the checkpoint name text region (in pt) */
  checkpointNameFontSize?: number;
  /** Font size override for the form name text region (in pt) */
  formNameFontSize?: number;
  /** Font size override for the serial number text region (in pt) */
  serialNumberFontSize?: number;
  /** jsPDF font family: 'helvetica' | 'times' | 'courier' */
  fontFamily?: string;
  /** QR code X position override (0.0–1.0 fraction; if omitted uses layout config) */
  qrX?: number;
  /** QR code Y position override (0.0–1.0 fraction; if omitted uses layout config) */
  qrY?: number;
  /** QR code width override (0.0–1.0 fraction; if omitted uses layout config) */
  qrWidth?: number;
  /** QR code height override in mm */
  qrHeight?: number;
  /** Checkpoint name text X position override (0.0–1.0 fraction) */
  checkpointNameX?: number;
  /** Checkpoint name text Y position override (0.0–1.0 fraction) */
  checkpointNameY?: number;
  /** Checkpoint name text width override (0.0–1.0 fraction) */
  checkpointNameWidth?: number;
  /** Checkpoint name text height override in mm */
  checkpointNameHeight?: number;
  /** Form name text X position override (0.0–1.0 fraction) */
  formNameX?: number;
  /** Form name text Y position override (0.0–1.0 fraction) */
  formNameY?: number;
  /** Form name text width override (0.0–1.0 fraction) */
  formNameWidth?: number;
  /** Form name text height override in mm */
  formNameHeight?: number;
  /** Serial number text X position override (0.0–1.0 fraction) */
  serialNumberX?: number;
  /** Serial number text Y position override (0.0–1.0 fraction) */
  serialNumberY?: number;
  /** Serial number text width override (0.0–1.0 fraction) */
  serialNumberWidth?: number;
  /** Serial number text height override in mm */
  serialNumberHeight?: number;
  /** Draw debug outlines around label regions */
  debug?: boolean;
  /** Instance assignments: position index → AssetInstance. When present, each cell gets a unique QR code. */
  instanceAssignments?: Map<number, AssetInstance>;
}

/**
 * Service for generating label PDFs using jsPDF.
 *
 * Takes a sheet format, label layout, set of selected positions, and
 * content data. Generates a PDF with labels rendered only at selected
 * positions. Unselected positions are left blank.
 */
@Injectable({
  providedIn: 'root',
})
export class LabelPdfService {
  private logger = inject(LoggerService).getLogger('label-pdf');

  /**
   * Generate a PDF document with labels at the selected positions.
   * Returns the jsPDF document ready for printing or download.
   */
  async generatePdf(params: LabelPdfParams): Promise<jsPDF> {
    const {
      sheetFormat,
      layout,
      selectedPositions,
      checkpointName,
      formName,
      qrUrl,
      codeType = 'qr',
      barcodeValues,
      sharedBarcodeValue,
      showCheckpointName,
      showFormName,
      showCode,
      showSerialNumber,
      checkpointNameFontSize,
      formNameFontSize,
      serialNumberFontSize,
      fontFamily,
      qrX,
      qrY,
      qrWidth,
      qrHeight,
      checkpointNameX,
      checkpointNameY,
      checkpointNameWidth,
      checkpointNameHeight,
      formNameX,
      formNameY,
      formNameWidth,
      formNameHeight,
      serialNumberX,
      serialNumberY,
      serialNumberWidth,
      serialNumberHeight,
      debug,
      instanceAssignments,
    } = params;

    this.logger.debug('Generating label PDF', {
      format: sheetFormat.id,
      layout: layout.id,
      positions: selectedPositions.size,
      codeType,
      hasInstances: !!instanceAssignments?.size,
    });

    const doc = new jsPDF({
      unit: 'mm',
      format: [sheetFormat.page_width, sheetFormat.page_height],
      orientation: 'portrait',
    });

    // For simple (non-instanced) labels, generate one shared code image
    // For instanced labels, we generate per-instance images in the loop
    const hasInstances = instanceAssignments && instanceAssignments.size > 0;
    let sharedCodeDataUrl: string | null = null;
    if (!hasInstances) {
      if (codeType === 'barcode' && sharedBarcodeValue) {
        sharedCodeDataUrl = await this.generateBarcodeDataUrl(sharedBarcodeValue);
      } else {
        sharedCodeDataUrl = await this.generateQrDataUrl(qrUrl);
      }
    }

    // Cache generated code images by key to avoid regenerating
    const codeCache = new Map<string, string>();

    const config = layout.layoutConfig;

    // Inject a serialNumber text region if the layout doesn't define one but serial numbers are enabled
    if (
      showSerialNumber !== false &&
      hasInstances &&
      !config.textRegions.some((r) => r.field === 'serialNumber')
    ) {
      config.textRegions = [
        ...config.textRegions,
        {
          field: 'serialNumber' as const,
          x: 0.38,
          y: 0.75,
          width: 0.62,
          height: 0.25,
          fontSize: 8,
          fontWeight: 'normal' as const,
          align: 'left' as const,
          maxLines: 1,
        },
      ];
    }

    for (const position of selectedPositions) {
      const col = position % sheetFormat.columns;
      const row = Math.floor(position / sheetFormat.columns);

      // Skip out-of-bounds positions
      if (row >= sheetFormat.rows || col >= sheetFormat.columns) {
        continue;
      }

      // Determine code data URL and serial number for this position
      let codeDataUrl: string;
      let serialNumber = '';
      let isBarcode = codeType === 'barcode';
      if (hasInstances && instanceAssignments!.has(position)) {
        const instance = instanceAssignments!.get(position)!;
        serialNumber = instance.serial_number;
        const cacheKey = `${codeType}:${instance.id}`;
        if (codeCache.has(cacheKey)) {
          codeDataUrl = codeCache.get(cacheKey)!;
        } else if (isBarcode && barcodeValues?.has(position)) {
          codeDataUrl = await this.generateBarcodeDataUrl(barcodeValues.get(position)!);
          codeCache.set(cacheKey, codeDataUrl);
        } else {
          // Per-instance QR: append instanceId to base URL
          const instanceQrUrl = `${qrUrl}/${instance.id}`;
          codeDataUrl = await this.generateQrDataUrl(instanceQrUrl);
          codeCache.set(cacheKey, codeDataUrl);
        }
      } else if (isBarcode && barcodeValues?.has(position)) {
        const barcodeVal = barcodeValues.get(position)!;
        if (codeCache.has(barcodeVal)) {
          codeDataUrl = codeCache.get(barcodeVal)!;
        } else {
          codeDataUrl = await this.generateBarcodeDataUrl(barcodeVal);
          codeCache.set(barcodeVal, codeDataUrl);
        }
      } else {
        codeDataUrl = sharedCodeDataUrl || (await this.generateQrDataUrl(qrUrl));
        if (!sharedCodeDataUrl) sharedCodeDataUrl = codeDataUrl;
      }

      // Calculate label top-left corner in mm
      const labelX =
        sheetFormat.margin_left + col * (sheetFormat.label_width + sheetFormat.gutter_horizontal);
      const labelY =
        sheetFormat.margin_top + row * (sheetFormat.label_height + sheetFormat.gutter_vertical);

      this.renderLabel(doc, {
        labelX,
        labelY,
        labelWidth: sheetFormat.label_width,
        labelHeight: sheetFormat.label_height,
        config,
        codeDataUrl,
        isBarcode,
        checkpointName,
        formName,
        serialNumber,
        showCheckpointName,
        showFormName,
        showCode,
        showSerialNumber,
        checkpointNameFontSize,
        formNameFontSize,
        serialNumberFontSize,
        fontFamily,
        qrX,
        qrY,
        qrWidth,
        qrHeight,
        checkpointNameX,
        checkpointNameY,
        checkpointNameWidth,
        checkpointNameHeight,
        formNameX,
        formNameY,
        formNameWidth,
        formNameHeight,
        serialNumberX,
        serialNumberY,
        serialNumberWidth,
        serialNumberHeight,
        debug,
      });
    }

    this.logger.debug('PDF generation complete');
    return doc;
  }

  /**
   * Open the generated PDF in a new browser window and trigger print.
   */
  printPdf(doc: jsPDF): void {
    doc.autoPrint();
    const blobUrl = doc.output('bloburl');
    window.open(blobUrl as unknown as string, '_blank');
  }

  // ---------------------------------------------------------------------------
  // Private rendering
  // ---------------------------------------------------------------------------

  private renderLabel(
    doc: jsPDF,
    params: {
      labelX: number;
      labelY: number;
      labelWidth: number;
      labelHeight: number;
      config: LabelLayoutConfig;
      codeDataUrl: string;
      isBarcode: boolean;
      checkpointName: string;
      formName: string;
      serialNumber: string;
      showCheckpointName?: boolean;
      showFormName?: boolean;
      showCode?: boolean;
      showSerialNumber?: boolean;
      checkpointNameFontSize?: number;
      formNameFontSize?: number;
      serialNumberFontSize?: number;
      fontFamily?: string;
      qrX?: number;
      qrY?: number;
      qrWidth?: number;
      qrHeight?: number;
      checkpointNameX?: number;
      checkpointNameY?: number;
      checkpointNameWidth?: number;
      checkpointNameHeight?: number;
      formNameX?: number;
      formNameY?: number;
      formNameWidth?: number;
      formNameHeight?: number;
      serialNumberX?: number;
      serialNumberY?: number;
      serialNumberWidth?: number;
      serialNumberHeight?: number;
      debug?: boolean;
    },
  ): void {
    const {
      labelX,
      labelY,
      labelWidth,
      labelHeight,
      config,
      codeDataUrl,
      isBarcode,
      checkpointName,
      formName,
      serialNumber,
      showCheckpointName,
      showFormName,
      showCode,
      showSerialNumber,
      checkpointNameFontSize,
      formNameFontSize,
      serialNumberFontSize,
      fontFamily,
      debug,
    } = params;

    // Usable area after padding
    const pad = config.padding;
    const usableX = labelX + pad.left;
    const usableY = labelY + pad.top;
    const usableWidth = labelWidth - pad.left - pad.right;
    const usableHeight = labelHeight - pad.top - pad.bottom;

    // Clip all rendering to the label boundary so overflowing text never bleeds into adjacent labels
    const docInternal = (doc as any).internal;
    const k: number = docInternal.scaleFactor; // mm to PDF internal points
    const pageH: number = docInternal.pageSize.getHeight(); // mm
    docInternal.out('q');
    const clipX = (labelX * k).toFixed(3);
    const clipY = ((pageH - labelY - labelHeight) * k).toFixed(3);
    const clipW = (labelWidth * k).toFixed(3);
    const clipH = (labelHeight * k).toFixed(3);
    docInternal.out(`${clipX} ${clipY} ${clipW} ${clipH} re W n`);

    // Debug: draw region outlines
    if (debug) {
      doc.setLineWidth(0.2);
      // Label outer boundary — red
      doc.setDrawColor(220, 50, 50);
      doc.rect(labelX, labelY, labelWidth, labelHeight, 'S');
      // Usable area (after padding) — blue
      doc.setDrawColor(50, 100, 220);
      doc.rect(usableX, usableY, usableWidth, usableHeight, 'S');
    }

    // Render code image (QR code or barcode)
    // All position values are fractions of the full label (origin at label top-left corner).
    // Falls back to layout config defaults, converting from usable-area fractions.
    const qr = config.qr;
    const qrX = labelX + (params.qrX ?? pad.left + qr.x * usableWidth);
    const qrY = labelY + (params.qrY ?? pad.top + qr.y * usableHeight);
    const qrWidth = params.qrWidth ?? qr.width * usableWidth;
    const qrHeight = params.qrHeight ?? qr.height * usableHeight;

    if (showCode !== false) {
      try {
        if (isBarcode) {
          // Barcode: rectangular — fill width of region, center vertically
          const barcodeWidth = qrWidth;
          const barcodeHeight = Math.min(qrHeight, qrWidth * 0.4); // barcode height proportional
          const barcodeOffsetY = (qrHeight - barcodeHeight) / 2;
          doc.addImage(codeDataUrl, 'PNG', qrX, qrY + barcodeOffsetY, barcodeWidth, barcodeHeight);
        } else {
          // QR code: square — use the smaller dimension
          const qrSize = Math.min(qrWidth, qrHeight);
          const qrOffsetY = (qrHeight - qrSize) / 2;
          doc.addImage(codeDataUrl, 'PNG', qrX, qrY + qrOffsetY, qrSize, qrSize);
        }
      } catch (err) {
        this.logger.warn('Failed to add code image to PDF:', err);
      }
    }

    // Debug: QR region outline — green
    if (debug && showCode !== false) {
      doc.setLineWidth(0.2);
      doc.setDrawColor(50, 180, 50);
      doc.rect(qrX, qrY, qrWidth, qrHeight, 'S');
    }

    // Render text regions
    for (const region of config.textRegions) {
      const isCheckpointName = region.field === 'checkpointName';
      const isFormName = region.field === 'formName';
      const isSerialNumber = region.field === 'serialNumber';

      if (isCheckpointName && showCheckpointName === false) continue;
      if (isFormName && showFormName === false) continue;
      if (isSerialNumber && showSerialNumber === false) continue;
      if (isSerialNumber && !serialNumber) continue;

      const text = isCheckpointName ? checkpointName : isSerialNumber ? serialNumber : formName;
      if (!text) continue;

      let textX: number;
      let textY: number;
      let textWidth: number;

      if (isCheckpointName) {
        textX = labelX + (params.checkpointNameX ?? pad.left + region.x * usableWidth);
        textY = labelY + (params.checkpointNameY ?? pad.top + region.y * usableHeight);
        textWidth = params.checkpointNameWidth ?? region.width * usableWidth;
      } else if (isSerialNumber) {
        textX = labelX + (params.serialNumberX ?? pad.left + region.x * usableWidth);
        textY = labelY + (params.serialNumberY ?? pad.top + region.y * usableHeight);
        textWidth = params.serialNumberWidth ?? region.width * usableWidth;
      } else {
        textX = labelX + (params.formNameX ?? pad.left + region.x * usableWidth);
        textY = labelY + (params.formNameY ?? pad.top + region.y * usableHeight);
        textWidth = params.formNameWidth ?? region.width * usableWidth;
      }

      const textHeight = isCheckpointName
        ? (params.checkpointNameHeight ?? region.height * usableHeight)
        : isSerialNumber
          ? (params.serialNumberHeight ?? region.height * usableHeight)
          : (params.formNameHeight ?? region.height * usableHeight);

      // Debug: text region outline — orange
      if (debug) {
        doc.setLineWidth(0.2);
        doc.setDrawColor(220, 130, 50);
        doc.rect(textX, textY, textWidth, textHeight, 'S');
      }

      // Use user-selected font size if provided, otherwise auto-scale
      const fontSizeOverride = isCheckpointName
        ? checkpointNameFontSize
        : isSerialNumber
          ? serialNumberFontSize
          : formNameFontSize;
      const scaledFontSize = fontSizeOverride
        ? fontSizeOverride
        : this.computeAutoTextSizePt(
            text,
            textWidth,
            textHeight,
            region.maxLines ?? 1,
            region.fontSize,
          );

      doc.setFontSize(scaledFontSize);
      const pdfFont = fontFamily || 'helvetica';
      doc.setFont(pdfFont, region.fontWeight === 'bold' ? 'bold' : 'normal');

      // Place baseline at top of text region so wrapped lines flow downward within it
      const lineHeight = scaledFontSize * 0.3528; // pt to mm
      const baselineY = textY + lineHeight;

      doc.text(text, textX, baselineY, {
        maxWidth: textWidth,
        align: region.align || 'left',
      });
    }

    // Restore graphics state — lifts the label boundary clip
    docInternal.out('Q');
  }

  private computeAutoTextSizePt(
    text: string,
    widthMm: number,
    heightMm: number,
    maxLines: number,
    fallbackSize: number,
  ): number {
    const safeText = text.trim();
    if (!safeText) {
      return fallbackSize;
    }

    const chars = Math.max(1, safeText.length);
    const lineLimit = Math.max(1, maxLines);
    const usableWidth = Math.max(1, widthMm);
    const usableHeight = Math.max(1, heightMm);

    const fits = (fontSizePt: number): boolean => {
      const charWidthMm = fontSizePt * 0.3528 * 0.56;
      const charsPerLine = Math.max(1, Math.floor(usableWidth / charWidthMm));
      const lines = Math.ceil(chars / charsPerLine);
      const textHeightMm = lines * fontSizePt * 0.3528 * 1.15;
      return lines <= lineLimit && textHeightMm <= usableHeight;
    };

    let low = 4;
    let high = 48;
    let best = 4;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (fits(mid)) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    return best;
  }

  /**
   * Generate a QR code as a PNG data URL using a canvas element.
   * Uses the 'qrcode' library (transitive dependency via angularx-qrcode).
   */
  private async generateQrDataUrl(data: string): Promise<string> {
    const canvas = document.createElement('canvas');
    const size = 256;
    canvas.width = size;
    canvas.height = size;

    try {
      // qrcode is a CommonJS module (transitive dep via angularx-qrcode)
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const QRCode = await import('qrcode' as any);
      const toCanvas = QRCode.toCanvas || QRCode.default?.toCanvas;
      if (toCanvas) {
        await toCanvas(canvas, data, {
          width: size,
          margin: 1,
          errorCorrectionLevel: 'M',
        });
        return canvas.toDataURL('image/png');
      }
      throw new Error('toCanvas not found on qrcode module');
    } catch (err) {
      this.logger.warn('QRCode canvas generation failed, using fallback:', err);
      return this.createPlaceholderQr(canvas, size);
    }
  }

  private createPlaceholderQr(canvas: HTMLCanvasElement, size: number): string {
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(4, 4, size - 8, size - 8);
      ctx.fillStyle = '#000000';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('QR', size / 2, size / 2 + 5);
    }
    return canvas.toDataURL('image/png');
  }

  /**
   * Generate a 1D barcode as a PNG data URL using JsBarcode.
   */
  private async generateBarcodeDataUrl(value: string): Promise<string> {
    try {
      const JsBarcode = (await import('jsbarcode')).default;
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, value, {
        format: 'CODE128',
        width: 2,
        height: 60,
        displayValue: true,
        fontSize: 14,
        margin: 4,
        font: 'monospace',
      });
      return canvas.toDataURL('image/png');
    } catch (err) {
      this.logger.warn('Barcode generation failed:', err);
      return this.createPlaceholderBarcode(value);
    }
  }

  private createPlaceholderBarcode(value: string): string {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 80;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, 200, 80);
      ctx.fillStyle = '#000000';
      // Draw simple bars
      for (let i = 0; i < 40; i++) {
        if (i % 2 === 0) {
          ctx.fillRect(10 + i * 4.5, 5, 3, 50);
        }
      }
      ctx.font = '12px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(value, 100, 72);
    }
    return canvas.toDataURL('image/png');
  }
}
